import { useApplyCouponMutation, useRemoveDiscountMutation } from '@/src/api/rtkQuery/couponApi'
import { useApplyGiftVoucherMutation, useRemoveGiftVoucherMutation } from '@/src/api/rtkQuery/giftVoucherApi'
import {
    useGetApplicableCouponsListQuery,
    useGetItineraryDataQuery,
    type ApplicableCouponTagItem
} from '@/src/api/rtkQuery/itineraryApi'
import useGetItineraryId from '@/src/hooks/itinerary/useGetItineraryId'
import type { CouponDetail, CouponErrorDetails, GiftVoucherDetail, InputField } from '@/src/types/couponTray'

import { CashbackSchedule } from '@/src/types/couponTray'
import { VARIANT } from '@/src/utils/buildUtils'
import { featureConfig } from '@/src/utils/featureConfig'
import { isEgDomain } from '@/src/utils/generalUtils'
import { formatCouponMessages } from '@/src/utils/itinerary/couponUtils'
import { useLocale, useTranslations } from 'next-intl'
import { useState } from 'react'
import useClevertapForItinerary from '../analytics/clevertap/itinerary'
import useItineraryGtmEvent from '../analytics/gtm/itinerary'
import useFormatCurrencyForItinerary from '../currency/useFormatCurrencyForItinerary'
import useFormatDate from '../date/useFormatDate'

/** Gift voucher detection: first 6 chars of code must match one of these (AE adds 110209). */
const GV_PREFIX_PATTERNS_BASE = [
    '100137',
    '300013',
    '300033',
    '600137',
    '100147',
    '500400',
    '110009',
    '112006',
    '115006'
] as const

const getGvPrefixPatterns = (locale: string): string[] =>
    locale === 'AE'
        ? [...GV_PREFIX_PATTERNS_BASE, '110209']
        : [...GV_PREFIX_PATTERNS_BASE]

export const isGiftVoucher = (value: string, locale: string): boolean => {
    if (!value || value.length < 6) return false
    const firstSix = value.substring(0, 6)
    return getGvPrefixPatterns(locale).includes(firstSix)
}

/** GV input: alphanumeric only, max 16 chars. */
const GV_INPUT_MAX_LENGTH = 16

/** Maximum number of gift vouchers that can be applied per booking. */
const MAX_GV_COUNT = 3
const sanitizeGvInput = (raw: string): string =>
    raw.replace(/[^A-Za-z0-9]/g, '').slice(0, GV_INPUT_MAX_LENGTH)

const initialInputField = (): InputField => ({
    value: '',
    isApplied: false,
    error: null,
    pinDetails: { required: false, pinValue: '' }
})

function getMutationErrorMessage(err: unknown, fallback: string): string {
    const e = err as { data?: { message?: string }; message?: string }
    return e?.data?.message ?? e?.message ?? fallback
}

const useCouponTray = (onSuccessfulApply?: (message: string) => void) => {
    const [typedInputField, setInputField] = useState<InputField>(initialInputField())
    const [errorDetails, setErrorDetails] = useState<CouponErrorDetails>({ error: false, msg: '', index: 0 })
    const [gvInputField, setGvInputField] = useState<InputField>(initialInputField())

    // Determine domain type
    const isEgyptDomain = isEgDomain()
    const isGiftVoucherEnabled = !!featureConfig[VARIANT]?.GIFT_VOUCHER

    const t = useTranslations('couponTray')
    const tVariants = useTranslations('variants')
    const locale = useLocale()
    const { formatCurrency } = useFormatCurrencyForItinerary()
    const { formatDate } = useFormatDate()
    const itineraryId = useGetItineraryId()
    const [applyCoupon, { isLoading: isApplying }] = useApplyCouponMutation()
    const [applyGiftVoucher, { isLoading: isApplyingGv }] = useApplyGiftVoucherMutation()
    const [removeDiscount] = useRemoveDiscountMutation()
    const [removeGiftVoucher] = useRemoveGiftVoucherMutation()
    const {
        couponDetails,
        isLoading: isItineraryLoading,
        showCoupon,
        giftVoucherData,
        metaCouponList,
        hasMetaCouponList
    } = useGetItineraryDataQuery(
        { itineraryId },
        {
            selectFromResult: ({ data, isLoading }) => ({
                couponDetails: data?.itineraryData?.couponDetails || [],
                showCoupon: data?.meta?.showCoupon,
                giftVoucherData: data?.itineraryData?.giftVoucherData ?? [],
                metaCouponList: data?.meta?.couponList,
                hasMetaCouponList: !!data?.meta && Object.prototype.hasOwnProperty.call(data.meta, 'couponList'),
                isLoading
            })
        }
    )
    const { triggerItineraryEvent } = useClevertapForItinerary()
    const { pushEventToDataLayer } = useItineraryGtmEvent()

    const rawMetaCouponList = metaCouponList as { tags?: { tags?: ApplicableCouponTagItem[] } } | null | undefined
    const metaCouponTags = Array.isArray(rawMetaCouponList?.tags?.tags) ? rawMetaCouponList.tags.tags : []
    const shouldFetchApplicableCoupons = !isItineraryLoading && !hasMetaCouponList
    const { data: applicableCouponTags = [], isLoading: isLoadingApplicableCoupons } = useGetApplicableCouponsListQuery(
        { itineraryId },
        { skip: !shouldFetchApplicableCoupons }
    )
    const couponTags = hasMetaCouponList ? metaCouponTags : applicableCouponTags

    // Transform coupon tag data to offer format with fallback to en if ar is empty.
    const mappedOffers = couponTags
        .map(tag => {
            return {
                id: tag._id,
                icon: tag.icon,
                logo: tag.icon, // Legacy property
                amount: tag.amount,
                prioritySort: !!tag.prioritySort,
                code: tag.code,
                knowMoreLink: tag.targetLink,
                en: {
                    heading: tag.en?.heading || '',
                    description: tag.en?.description || ''
                },
                ar: {
                    heading: tag.ar?.heading || tag.en?.heading || '',
                    description: tag.ar?.description || tag.en?.description || ''
                }
            }
        })
    const priorityOffers = mappedOffers
        .filter(offer => offer.prioritySort)
        .sort((a, b) => (b.amount ?? 0) - (a.amount ?? 0))
    const nonPriorityOffers = mappedOffers
        .filter(offer => !offer.prioritySort)
        .sort((a, b) => (b.amount ?? 0) - (a.amount ?? 0))
    const offers = [...priorityOffers, ...nonPriorityOffers]
    const appliedCouponCode = couponDetails[0]?.couponCode || ''
    const successMessages = formatCouponMessages(couponDetails, formatCurrency)
    const gvSuccessMessage =
        isGiftVoucherEnabled && giftVoucherData?.length > 0
            ? (() => {
                  const total = giftVoucherData
                      .filter(v => v.voucherStatus === 'SUCCESS')
                      .reduce((sum, v) => sum + (v.amount ?? 0), 0)
                  return total > 0 ? t('gvRedeemedCallout', { amount: formatCurrency(total) }) : undefined
              })()
            : undefined
    const inputField = couponDetails[0]?.couponCode || ''
    const walletPromoDetail = couponDetails.find((couponDetail: CouponDetail) => couponDetail.walletPromo)

    const extractCashbackSchedule = (walletPromoDetail: CouponDetail): CashbackSchedule => {
        const { message_values, amount, currency, breakup = [] } = walletPromoDetail
        const date = message_values?.date || ''
        const wallet = message_values?.wallet || ''
        const variant = tVariants(wallet.toLowerCase())

        let message = ''
        if (breakup.length > 0) {
            const b = breakup[0]
            message = t('cashbackWalletMessage', {
                cashbackAmount: formatCurrency(b.amount),
                variant,
                date: formatDate(b.date)
            })
        } else if (amount && wallet && date) {
            message = t('cashbackWalletMessage', {
                cashbackAmount: formatCurrency(amount),
                variant,
                date: formatDate(date)
            })
        } else {
            message = walletPromoDetail.detailedMessage || walletPromoDetail.message || ''
        }

        return {
            message,
            date,
            amount,
            breakup,
            currency
        }
    }

    const cashbackSchedule = walletPromoDetail ? extractCashbackSchedule(walletPromoDetail) : null

    const handleInputChange = (_: number, value: string) => {
        setInputField(field => ({ ...field, value, error: null }))
        if (isGiftVoucherEnabled) {
            const trimmed = value.trim()
            setGvInputField(prev => ({
                ...prev,
                pinDetails: {
                    ...(prev.pinDetails ?? { required: false, pinValue: '' }),
                    required: isGiftVoucher(trimmed, locale) && trimmed.length > 9
                }
            }))
        }
        setErrorDetails({ error: false, msg: '', index: 0 })
    }

    const handleApply = async ({ code, source, index }: { code: string; source: string; index: number }) => {
        if (!code) {
            setErrorDetails({ error: true, msg: t('emptyError'), index: 0 })
            return
        }
        const value = code.trim()
        setInputField(field => ({ ...field, value: code }))
        const actionName = 'click_flight_coupon_apply'
        const isQuickCouponApplied = source === 'couponTray' ? 'yes' : 'no'

        try {
            // Gift voucher: applying coupon removes any applied GV first (coupon and GV are mutually exclusive)
            if (isGiftVoucherEnabled && giftVoucherData?.length) {
                const removePayload = giftVoucherData.map(v => ({ code: v.voucherCode, pin: v.pin }))
                await removeGiftVoucher({ itineraryId, payload: removePayload }).unwrap()
            }

            const response = await applyCoupon({
                itineraryId,
                payload: { couponCodes: [value], validatePayments: false }
            })

            if (response?.data && Array.isArray(response?.data?.couponData)) {
                triggerItineraryEvent('Air_UI_Action', {
                    action_type: 'BUTTON',
                    action_name: actionName,
                    coupon_name: value,
                    coupon_index: index,
                    is_success: true
                })
                pushEventToDataLayer({
                    event: actionName,
                    coupon_name: value,
                    coupon_index: index,
                    is_success: true,
                    coupon_source: source
                })
                setErrorDetails({ error: false, msg: '', index: 0 })
                // Handle successful apply with success message
                // Get the success message from the API response
                const appliedCouponData = response.data.couponData[0]
                let message = t('successMessage')
                if (appliedCouponData?.message) {
                    message = appliedCouponData.message
                    if (message.includes('<amount>')) {
                        message = message.replace('<amount>', String(formatCurrency(appliedCouponData.amount)))
                    }
                }
                onSuccessfulApply?.(message)
            } else if (response?.data && Array.isArray(response?.data?.errorCouponData)) {
                const errorMessage = response.data.errorCouponData[0]?.message || t('errorMessage')
                triggerItineraryEvent('Air_Failures', {
                    coupon_name: value,
                    coupon_index: index,
                    failure_reason: errorMessage,
                    quick_coupon_applied: isQuickCouponApplied,
                    failure_type: 'coupon_failure'
                })
                pushEventToDataLayer({
                    event: actionName,
                    coupon_name: value,
                    coupon_index: index,
                    is_success: false,
                    coupon_source: source
                })
                setErrorDetails({
                    error: true,
                    msg: errorMessage,
                    index: 0
                })
            } else {
                const fallbackMsg = t('errorMessage')
                triggerItineraryEvent('Air_UI_Action', {
                    coupon_name: value,
                    coupon_index: index,
                    failure_reason: fallbackMsg,
                    quick_coupon_applied: isQuickCouponApplied,
                    failure_type: 'coupon_failure'
                })
                pushEventToDataLayer({
                    event: actionName,
                    coupon_name: value,
                    coupon_index: index,
                    is_success: false,
                    coupon_source: source
                })
                setErrorDetails({
                    error: true,
                    msg: fallbackMsg,
                    index: 0
                })
            }
        } catch (e) {
            const fallbackMsg = t('errorMessage')
            triggerItineraryEvent('Air_Failures', {
                coupon_name: value,
                coupon_index: index,
                failure_reason: fallbackMsg,
                quick_coupon_applied: isQuickCouponApplied,
                failure_type: 'coupon_failure'
            })
            pushEventToDataLayer({
                event: actionName,
                coupon_name: value,
                coupon_index: index,
                is_success: false,
                coupon_source: source
            })
            setErrorDetails({ error: true, msg: fallbackMsg, index: 0 })
        }
    }

    const handleRemove = async (_: number) => {
        try {
            const response = await removeDiscount({
                itineraryId,
                payload: { remove: ['COUPON'] }
            })

            if (response?.data?.data?.coupon === 'success') {
                setInputField(initialInputField())
                setErrorDetails({ error: false, msg: '', index: 0 })
            } else {
                setErrorDetails({ error: true, msg: t('errorMessage'), index: 0 })
            }
        } catch (e) {
            setErrorDetails({ error: true, msg: t('errorMessage'), index: 0 })
        }
    }

    const handleKnowMore = (_offerId: string) => {}

    const handleGvInputChange = (value?: string, pinValue?: string) => {
        setGvInputField(prev => {
            const prevPin = prev.pinDetails ?? { required: false, pinValue: '' }
            // PIN-only update: code lives in typedInputField, so don't derive pinRequired from prev.value (empty)
            if (value === undefined && pinValue !== undefined) {
                return {
                    ...prev,
                    pinDetails: { ...prevPin, pinValue: sanitizeGvInput(pinValue) }
                }
            }
            const nextValue = value !== undefined ? sanitizeGvInput(value) : prev.value
            const nextPinValue = pinValue !== undefined ? sanitizeGvInput(pinValue) : prevPin.pinValue
            const isGv = isGiftVoucher(nextValue, locale)
            const pinRequired = isGv && nextValue.length > 9
            const nextPin = {
                required: pinRequired,
                pinValue: pinRequired ? nextPinValue : ''
            }
            return {
                ...prev,
                ...(value !== undefined && { value: nextValue }),
                pinDetails: {
                    ...prevPin,
                    ...(pinValue !== undefined && { pinValue: nextPinValue }),
                    ...nextPin
                }
            }
        })
        setErrorDetails(prev => (prev.error ? { ...prev, error: false, msg: '' } : prev))
    }

    const applyGvHandler = async (payload: { code: string; pin: string }) => {
        if (giftVoucherData.length >= MAX_GV_COUNT) {
            setErrorDetails({ error: true, msg: t('gvMaxReached'), index: 0 })
            return
        }
        const code = payload.code?.trim() ?? ''
        const pin = payload.pin?.trim() ?? ''
        if (!code) {
            setErrorDetails({ error: true, msg: t('emptyError'), index: 0 })
            return
        }
        const isGv = isGiftVoucher(code, locale)
        const pinRequired = isGv && code.length > 9
        if (pinRequired && !pin) {
            setErrorDetails({ error: true, msg: t('gvCardOrPinEmpty'), index: 0 })
            return
        }
        if (isGv && code.length <= 9) {
            setErrorDetails({ error: true, msg: t('gvInvalidCardNumber'), index: 0 })
            return
        }

        try {
            const data = await applyGiftVoucher({
                itineraryId,
                payload: { voucherDetails: [{ code, pin }] }
            }).unwrap()
            const v = data?.length ? data[0] : null
            if (v?.voucherStatus === 'FAILURE') {
                setErrorDetails({
                    error: true,
                    msg: v.failureMessage ?? t('errorMessage'),
                    index: 0
                })
                return
            }
            setGvInputField(initialInputField())
            setInputField(initialInputField())
            setErrorDetails({ error: false, msg: '', index: 0 })
            const message =
                v?.voucherStatus === 'SUCCESS' && v.amount != null
                    ? t('gvAppliedSuccess', { amount: formatCurrency(v.amount), currency: v.currency ?? '' })
                    : t('successMessage')
            onSuccessfulApply?.(message)
        } catch (err: unknown) {
            setErrorDetails({ error: true, msg: getMutationErrorMessage(err, t('errorMessage')), index: 0 })
        }
    }

    const removeGvHandler = async (voucher: GiftVoucherDetail) => {
        try {
            await removeGiftVoucher({
                itineraryId,
                payload: [{ code: voucher.voucherCode, pin: voucher.pin }]
            }).unwrap()
            setErrorDetails({ error: false, msg: '', index: 0 })
        } catch (err: unknown) {
            setErrorDetails({ error: true, msg: getMutationErrorMessage(err, t('errorMessage')), index: 0 })
        }
    }

    return {
        inputField,
        typedInputField,
        offers,
        onInputChange: handleInputChange,
        onApply: handleApply,
        onRemove: handleRemove,
        onKnowMore: handleKnowMore,
        formatCurrency,
        isLoading: isItineraryLoading || isLoadingApplicableCoupons,
        isApplying,
        isApplyingGv,
        successMessages,
        appliedCouponCode,
        errorDetails,
        t,
        cashbackSchedule,
        showCoupon,
        giftVoucherData,
        // Gift voucher: one block first; PIN + Apply below only when GV pattern (or after one GV applied)
        showGvPinBlock:
            isGiftVoucherEnabled &&
            couponDetails.length === 0 &&
            (giftVoucherData.length > 0 ||
                (isGiftVoucher(typedInputField.value.trim(), locale) && typedInputField.value.trim().length > 9)),
        canApplyMoreGv: isGiftVoucherEnabled && giftVoucherData.length < MAX_GV_COUNT,
        gvSuccessMessage: gvSuccessMessage ?? undefined,
        gvPinValue: gvInputField.pinDetails?.pinValue ?? '',
        onGvPinChange: isGiftVoucherEnabled && couponDetails.length === 0 ? (pinValue: string) => handleGvInputChange(undefined, pinValue) : undefined,
        onApplyGv: isGiftVoucherEnabled && couponDetails.length === 0 ? applyGvHandler : undefined,
        onRemoveGv: isGiftVoucherEnabled && couponDetails.length === 0 ? removeGvHandler : undefined
    }
}

export default useCouponTray
